import type { Currency } from '../../types/Currency';
import type { Security } from '../../types/Security';
import { assertNever } from '../../utils/assertNever';
import { JSON_MODAL_PAYLOAD_KIND, gridRowDocument, type JsonModalDocument } from './types';

export interface RelatedEntitiesLookupMaps {
  currenciesBySymbol: Map<string, Currency>;
  securitiesBySymbol: Map<string, Security>;
}

export type RelatedEntitySymbolLookup = 'security' | 'currency';

export interface RelatedEntitySymbolSource<R> {
  id: string;
  title: string;
  getSymbol(data: R, maps: RelatedEntitiesLookupMaps): string | undefined;
  /** `'security'` / `'currency'` use that map only. Omitted: security, then currency (security wins on collision). */
  lookup?: RelatedEntitySymbolLookup;
  optional?: boolean;
}

export interface RelatedEntitiesConfig<R = unknown> {
  symbolSources?: readonly RelatedEntitySymbolSource<R>[];
  feedObjects?: (data: R) => JsonModalDocument[];
}

export const EMPTY_RELATED_ENTITIES_LOOKUP_MAPS: RelatedEntitiesLookupMaps = {
  currenciesBySymbol: new Map(),
  securitiesBySymbol: new Map(),
};

function resolveSymbolSourceDocument<R>(
  source: RelatedEntitySymbolSource<R>,
  data: R,
  maps: RelatedEntitiesLookupMaps
): JsonModalDocument | null {
  const symbol = source.getSymbol(data, maps);

  if (symbol == null || symbol.trim() === '') {
    if (source.optional) {
      return null;
    }

    return {
      id: source.id,
      title: source.title,
      value: undefined,
      emptyMessage: `No symbol at ${source.id}`,
    };
  }

  const value = resolveLookedUpValue(symbol, maps, source.lookup);

  if (value != null) {
    return {
      id: source.id,
      title: source.title,
      value,
    };
  }

  return {
    id: source.id,
    title: source.title,
    value: undefined,
    emptyMessage: missingLookupMessage(symbol, source.lookup),
  };
}

function resolveLookedUpValue(
  symbol: string,
  maps: RelatedEntitiesLookupMaps,
  lookup: RelatedEntitySymbolLookup | undefined
): Currency | Security | undefined {
  switch (lookup) {
    case 'security':
      return maps.securitiesBySymbol.get(symbol);
    case 'currency':
      return maps.currenciesBySymbol.get(symbol);
    case undefined: {
      const security = maps.securitiesBySymbol.get(symbol);
      if (security != null) {
        return security;
      }
      return maps.currenciesBySymbol.get(symbol);
    }
    default:
      assertNever(lookup);
  }
}

function missingLookupMessage(symbol: string, lookup: RelatedEntitySymbolLookup | undefined): string {
  switch (lookup) {
    case 'security':
      return `No Security for ${symbol}`;
    case 'currency':
      return `No Currency for ${symbol}`;
    case undefined:
      return `No Currency or Security for ${symbol}`;
    default:
      assertNever(lookup);
  }
}

export function resolveRelatedEntities<R>(
  data: R,
  config: RelatedEntitiesConfig<R>,
  maps: RelatedEntitiesLookupMaps
): JsonModalDocument[] {
  const documents: JsonModalDocument[] = [];

  if (config.feedObjects != null) {
    documents.push(...config.feedObjects(data));
  }

  for (const source of config.symbolSources ?? []) {
    const document = resolveSymbolSourceDocument(source, data, maps);
    if (document != null) {
      documents.push(document);
    }
  }

  return documents;
}

export function buildShowJsonPayload<R>(
  rowValue: unknown,
  data: R,
  config?: RelatedEntitiesConfig<R>,
  maps: RelatedEntitiesLookupMaps = EMPTY_RELATED_ENTITIES_LOOKUP_MAPS
): unknown {
  if (config == null) {
    return rowValue;
  }

  const related = resolveRelatedEntities(data, config, maps);
  if (related.length === 0) {
    return rowValue;
  }

  return {
    kind: JSON_MODAL_PAYLOAD_KIND,
    documents: [gridRowDocument(rowValue), ...related],
  };
}

export function resolveShowJsonMenuData<R>(
  data: R | R[],
  nodeData: R | undefined,
  config?: RelatedEntitiesConfig<R>,
  maps: RelatedEntitiesLookupMaps = EMPTY_RELATED_ENTITIES_LOOKUP_MAPS
): unknown {
  if (Array.isArray(data)) {
    return data;
  }

  return buildShowJsonPayload(data, nodeData ?? data, config, maps);
}
